import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

interface PreviewModalProps {
  url: string;
  isOpen: boolean;
  onClose: () => void;
}

const PreviewModal: React.FC<PreviewModalProps> = ({ url, isOpen, onClose }) => {
  const [device, setDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset state when URL changes
  useEffect(() => {
    if (isOpen) {
      setIsLoaded(false);
      setHasError(false);
      setIsLoading(true);
    }
  }, [url, isOpen]);

  // Escape key handler
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, handleEscape]);

  // Body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  // Focus trap
  useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.focus();
    }
  }, [isOpen]);

  const handleRefresh = useCallback(() => {
    if (iframeRef.current) {
      setIsLoaded(false);
      setHasError(false);
      setIsLoading(true);
      iframeRef.current.src = url;
    }
  }, [url]);

  const handleOpenExternal = useCallback(() => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, [url]);

  const handleIframeLoad = useCallback(() => {
    setIsLoaded(true);
    setIsLoading(false);
    setHasError(false);
  }, []);

  const handleIframeError = useCallback(() => {
    setIsLoaded(false);
    setIsLoading(false);
    setHasError(true);
  }, []);

  if (!isOpen) return null;

  const getFrameWidth = () => {
    switch (device) {
      case 'mobile': return '375px';
      case 'tablet': return '768px';
      default: return '100%';
    }
  };

  const getFrameHeight = () => {
    switch (device) {
      case 'mobile':
      case 'tablet': return '100%';
      default: return '100%';
    }
  };

  const getFrameClass = () => {
    return `preview-frame preview-frame--${device}`;
  };

  const modalContent = (
    <div 
      ref={modalRef}
      className="sw-modal preview-modal is-open" 
      role="dialog" 
      aria-modal="true" 
      aria-label="Website Preview"
      tabIndex={-1}
    >
      <div className="modal-backdrop" onClick={onClose} aria-hidden="true"></div>
      <div className="modal-content preview-content">
        <header className="preview-header">
          <div className="preview-header-left">
            <div className="device-switcher" role="radiogroup" aria-label="Device view">
              <button 
                className={`device-btn ${device === 'desktop' ? 'active' : ''}`} 
                onClick={() => setDevice('desktop')} 
                title="Desktop View"
                role="radio"
                aria-checked={device === 'desktop'}
              >
                <span aria-hidden="true">🖥️</span>
                <span className="sr-only">Desktop</span>
              </button>
              <button 
                className={`device-btn ${device === 'tablet' ? 'active' : ''}`} 
                onClick={() => setDevice('tablet')} 
                title="Tablet View"
                role="radio"
                aria-checked={device === 'tablet'}
              >
                <span aria-hidden="true">📱</span>
                <span className="sr-only">Tablet</span>
              </button>
              <button 
                className={`device-btn ${device === 'mobile' ? 'active' : ''}`} 
                onClick={() => setDevice('mobile')} 
                title="Mobile View"
                role="radio"
                aria-checked={device === 'mobile'}
              >
                <span aria-hidden="true">🤳</span>
                <span className="sr-only">Mobile</span>
              </button>
            </div>
            <div className="preview-url" title={url}>
              {url}
            </div>
          </div>
          <div className="preview-header-right">
            <button 
              className="preview-action-btn" 
              onClick={handleRefresh}
              title="Refresh Preview"
              aria-label="Refresh Preview"
            >
              <span aria-hidden="true">🔄</span>
            </button>
            <button 
              className="preview-action-btn" 
              onClick={handleOpenExternal}
              title="Open in New Tab"
              aria-label="Open in New Tab"
            >
              <span aria-hidden="true">↗️</span>
            </button>
            <button 
              className="close-btn preview-close-btn" 
              onClick={onClose} 
              aria-label="Close Preview"
            >
              &times;
            </button>
          </div>
        </header>
        <div className={`preview-body preview-body--${device}`}>
          {isLoading && !isLoaded && (
            <div className="preview-loader" aria-label="Loading preview">
              <div className="preview-spinner"></div>
              <span className="preview-loading-text">Loading preview...</span>
            </div>
          )}
          {hasError && (
            <div className="preview-error" role="alert">
              <span className="preview-error-icon">⚠️</span>
              <p className="preview-error-text">Failed to load preview</p>
              <button className="sw-btn sw-btn-secondary" onClick={handleRefresh}>
                Try Again
              </button>
            </div>
          )}
          <iframe
            ref={iframeRef}
            src={url}
            title="Website Preview"
            className={getFrameClass()}
            style={{ 
              width: getFrameWidth(), 
              height: getFrameHeight()
            }}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default PreviewModal;
